import { ElementType, JSXElementConstructor, Suspense, lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UncaughtError } from 'src/components/base/UncaughtError';
import { LoadingScreen } from '../components/loading-screen';

const Loadable =
  (
    Component: ElementType,
    Fallback: JSXElementConstructor<any> = LoadingScreen,
  ) =>
  (props: any) => (
    <Suspense fallback={<Fallback />}>
      <ErrorBoundary FallbackComponent={UncaughtError}>
        <Component {...props} />
      </ErrorBoundary>
    </Suspense>
  );

// AUTH
export const LoginPage = Loadable(
  lazy(() => import('../pages/auth/LoginPage')),
);
export const RegisterPage = Loadable(
  lazy(() => import('../pages/auth/RegisterPage')),
);
export const WorkspaceInvitePage = Loadable(
  lazy(() => import('../pages/auth/WorkspaceInvite/WorkspaceInvitePage')),
);
export const WorkspaceJoiningPage = Loadable(
  lazy(() => import('../pages/auth/WorkspaceInvite/WorkspaceJoiningPage')),
);
export const VerifyAccountPage = Loadable(
  lazy(() => import('../pages/auth/VerifyAccountPage')),
);
export const LoginWithTwoFactorPage = Loadable(
  lazy(() => import('../pages/auth/LoginWithTwoFactorPage')),
);
export const ForgotPasswordPage = Loadable(
  lazy(() => import('../pages/auth/ForgotPasswordPage')),
);
export const ResetPasswordPage = Loadable(
  lazy(() => import('../pages/auth/ResetPasswordPage')),
);

export const WizardWelcomePage = Loadable(
  lazy(() => import('../pages/WizardPage/WizardWelcomePage')),
);
export const WizardCreateWorkspacePage = Loadable(
  lazy(() => import('../pages/WizardPage/WizardCreateWorkspacePage')),
);
export const WizardUpgradePlanPage = Loadable(
  lazy(() => import('../pages/WizardPage/WizardUpgradePlanPage')),
);
export const WizardInviteMembersPage = Loadable(
  lazy(() => import('../pages/WizardPage/WizardInviteMembersPage')),
);
export const EnableTwoFactorPage = Loadable(
  lazy(() => import('../pages/dashboard/EnableTwoFactorPage')),
);
export const ForceChangePasswordPage = Loadable(
  lazy(() => import('../pages/auth/ForceChangePasswordPage')),
);

// APP
export const OverviewPage = Loadable(
  lazy(() => import('../pages/dashboard/OverviewPage/OverviewPage')),
);

// AI

export const AIPage = Loadable(
  lazy(() => import('../sections/@dashboard/ai/AIPage')),
);

export const AISchedulesPage = Loadable(
  lazy(() => import('../sections/@dashboard/ai/Schedule/AISchedulesPage')),
);

export const AIScheduleSummariesPage = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/ai/Schedule/AIScheduleSummaries/AIScheduleSummariesPage'
      ),
  ),
);

// CHAT

export const ChatPage = Loadable(
  lazy(() => import('../pages/dashboard/ChatPage')),
);

export const ThreadViewPage = Loadable(
  lazy(() => import('src/sections/@dashboard/chat/ThreadViewPage')),
  () => null,
);

export const NewThreadViewPage = Loadable(
  lazy(
    () => import('src/sections/@dashboard/chat/NewThreadPage/NewThreadPage'),
  ),
  () => null,
);

// BOARD

export const BoardPage = Loadable(
  lazy(() =>
    import('../sections/@dashboard/board/BoardPage').then((module) => ({
      default: module.BoardPage,
    })),
  ),
);

export const TableViewPage = Loadable(
  lazy(() =>
    import('../sections/@dashboard/board/BoardPage/TableViewPage').then(
      (module) => ({
        default: module.TableViewPage,
      }),
    ),
  ),
);

export const BoardTableRowDialogPage = Loadable(
  lazy(() => import('../pages/dashboard/BoardTableRowDialogPage')),
);

// TODOS

export const TodosPage = Loadable(
  lazy(() =>
    import('../pages/dashboard/TodosPage').then((module) => ({
      default: module.TodosPage,
    })),
  ),
);

// DOCS

export const DocsPage = Loadable(
  lazy(() =>
    import('../pages/dashboard/DocsPage').then((module) => ({
      default: module.DocsPage,
    })),
  ),
);

export const DocsTrashPage = Loadable(
  lazy(() =>
    import('../pages/dashboard/DocsPage').then((module) => ({
      default: module.DocsTrashPage,
    })),
  ),
);

export const DocsPublicPage = Loadable(
  lazy(() => import('../pages/dashboard/PublicDocsPage/DocsPublicPage')),
);

export const DocCommentsPickViewPage = Loadable(
  lazy(() => import('../pages/dashboard/DocsPage/DocCommentsPickViewPage')),
  () => null,
);

// DRIVE
export const DrivePage = Loadable(
  lazy(() => import('../pages/dashboard/DrivePage')),
);

export const DriveTrashPage = Loadable(
  lazy(() => import('../pages/dashboard/DriveTrashPage')),
);

// CALENDAR

export const CalendarPage = Loadable(
  lazy(() => import('../pages/dashboard/CalendarPage')),
);

// SETTINGS

export const PublicDrivePage = Loadable(
  lazy(() =>
    import('../pages/dashboard/PublicDrivePage').then((module) => ({
      default: module.PublicDrivePage,
    })),
  ),
);

export const SettingsWorkspaceGeneralPage = Loadable(
  lazy(() => import('../pages/SettingsPage/WorkspaceGeneralPage')),
);

export const SettingsWorkspaceBillingPage = Loadable(
  lazy(
    () =>
      import('../pages/SettingsPage/WorkspaceBillingPage/WorkspaceBillingPage'),
  ),
);

export const SettingsWorkspaceMembersPage = Loadable(
  lazy(() => import('../pages/SettingsPage/WorkspaceMembersPage')),
);

export const SettingsWorkspaceRolesPage = Loadable(
  lazy(() => import('../pages/SettingsPage/WorkspaceRolesPage')),
);

export const SettingsWorkspaceTeamsPage = Loadable(
  lazy(() => import('../pages/SettingsPage/WorkspaceTeamsPage')),
);

export const SettingsUserProfilePage = Loadable(
  lazy(() => import('../pages/SettingsPage/UserProfilePage')),
);

export const SettingsUserNotificationsPage = Loadable(
  lazy(() => import('../pages/SettingsPage/UserNotificationsPage')),
);

export const SettingsUserSecurityPage = Loadable(
  lazy(() => import('../pages/SettingsPage/UserSecurityPage')),
);

export const SettingsUserLanguage = Loadable(
  lazy(() => import('../pages/SettingsPage/LanguagePage')),
);

// ZEN

export const ZenPage = Loadable(lazy(() => import('../pages/zen/ZenPage')));

export const KanbanPage = Loadable(
  lazy(() => import('../pages/dashboard/KanbanPage')),
);

// TEST RENDER PAGE BY ROLE
export const PermissionDeniedPage = Loadable(
  lazy(() => import('../pages/dashboard/PermissionDeniedPage')),
);

// BLANK PAGE
export const BlankPage = Loadable(
  lazy(() => import('../pages/dashboard/BlankPage')),
);

// MAIN
export const Page500 = Loadable(lazy(() => import('../pages/Page500')));
export const Page403 = Loadable(lazy(() => import('../pages/Page403')));
export const Page404 = Loadable(lazy(() => import('../pages/Page404')));

export const PaymentPage = Loadable(lazy(() => import('../pages/PaymentPage')));
export const PlansPage = Loadable(lazy(() => import('../pages/PlansPage')));

export const ComingSoonPage = Loadable(
  lazy(() => import('../pages/ComingSoonPage')),
);

export const MaintenancePage = Loadable(
  lazy(() => import('../pages/MaintenancePage')),
);
